import type { BrandItem } from '@innovationdepartment/proxima-sdk-axios';
import { useProximaSDK } from 'hooks';
import { useCallback, useEffect, useRef } from 'react';
import type { BrandName } from '../store/brandNamesSlice';
import useCustomAudiencesStore from '../store/useCustomAudiencesStore';

/**
 * Names for brand ids, looked up through proxima-platform `GET /brand/batch`.
 *
 * Ids without a known name are queued with the store's `requestBrandNames`, which sends one batch call for every id queued in the same commit.
 * Returns a lookup that gives the brand's name, or undefined while it is unknown.
 * A deleted brand, or a failed lookup, stays unknown, and callers show the raw id.
 */
export const useBrandNames = (
  brandIds: readonly string[]
): ((brandId: string) => string | undefined) => {
  const brandsApi = useProximaSDK('BrandsApi', { trackLoading: false });
  /* useProximaSDK returns a new client each render; the ref keeps the effect keyed on ids only */
  const brandsApiRef = useRef(brandsApi);
  brandsApiRef.current = brandsApi;
  const brandNames = useCustomAudiencesStore(state => state.brandNames);
  const requestBrandNames = useCustomAudiencesStore(state => state.requestBrandNames);
  const idsKey = [...new Set(brandIds)].sort().join(',');

  useEffect(() => {
    if (!idsKey) return;
    requestBrandNames(idsKey.split(','), async ids => {
      const response = await brandsApiRef.current.getBrandBatch({ brandId: ids.join(',') });
      return (response.data ?? []).filter((brand): brand is BrandItem & BrandName =>
        Boolean(brand.brandId)
      );
    });
  }, [idsKey, requestBrandNames]);

  return useCallback((brandId: string) => brandNames[brandId], [brandNames]);
};
